import React, { useState } from 'react';

import { isAwaitingBed, DEATH_RECORD_ERROR_CODE } from '../../constants/admission';
import ConfirmationModal from '../common/ConfirmationModal/ConfirmationModal';
import { dodDeathLine, deathDodFyFallback, DOD_DEATH_FY_FALLBACK } from './discharge/dischargeModel';

const overlayCls = 'fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4';
const panelCls = 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl w-full max-w-lg max-h-[92vh] overflow-y-auto';
const btnPrimary = 'px-4 py-2 rounded-lg bg-teal-600 text-white text-sm font-semibold disabled:opacity-60';
const btnSecondary = 'px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 text-sm';
const inputCls = 'w-full px-2.5 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-sm';
const labelCls = 'block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1';

/**
 * Format a Date into the value a `<input type="datetime-local">` expects
 * ("YYYY-MM-DDTHH:mm", local time, no timezone suffix) — same shape
 * `AdmitPatientForm.jsx`'s local helper produces.
 */
function toDatetimeLocalValue(date) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/**
 * RecordDeathDialog (FE-1, death-record slice — mockup Variant A) — the
 * quiet "+ Record death" header link's own dialog, on the admission, never
 * inside Discharge. ONE upsert route (EP-D1) drives both create AND edit;
 * this component owns both — Remove is a separate, form-less confirmation
 * the caller (AdmissionWorkspace's banner "Remove" button, FE-2) wires
 * directly against `deleteDeathRecord`, no form needed.
 *
 * S4 — this dialog captures `death_at` + the declaring doctor ONLY. The
 * cause of death is never asked here: Variant A's whole point is that a
 * cause is not knowable at 02:00 on a ward. It is captured later, in the
 * discharge dialog (FE-6), and lands in the SAME record through the SAME
 * writer.
 *
 * `declaringDoctorOptions` arrives as a PROP — the caller already fetches
 * the roster via the session-cached, single-flight `fetchStaffList` +
 * `buildDeclaringDoctorOptions` (constants/admission.js); this dialog opens
 * NO fetch of its own (runtime-safety rule).
 *
 * `onSave` (not a direct API import) mirrors the DI convention every other
 * admission dialog in this folder uses (`DischargeDialog.onComplete`,
 * `ReturnAdvanceDialog.onSubmit`, `TakeDepositDialog.onSubmit`) — the caller
 * (AdmissionWorkspace, FE-2) owns the network call so it can also patch its
 * own local admission state from the SAME response, in one place.
 *
 * Confirmation shows consequences, not warnings (owner/HMS-stakeholder
 * direction) via the shared `ConfirmationModal` — never a native dialog.
 *
 * @param admission               { admission_id, patient_name, ip_number, uhid?, patient_id,
 *                                   bed_label, ward_name, current_bed_id, admitted_at,
 *                                   cause_of_death, death_rev } — `uhid` is the REAL
 *                                   patient UHID (`AdmissionResponse.uhid`, backend
 *                                   mid-flight) or absent for a pre-2026-08-03 patient
 *                                   (D35, no backfill). NEVER `deriveUhid(patient_id)` —
 *                                   a database-key fragment on a wrong-patient guard is
 *                                   worse than a plainly-labelled absence (D34).
 * @param declaringDoctorOptions  [{member_id, name}] — DOCTOR-only, ACTIVE-only (S3 — the
 *                                   server resolves the display name from this id; the
 *                                   client's text is never trusted, never stored)
 * @param onClose                 ()
 * @param onSave(body)            → { success, data?: { admission, death_rev }, error?, code?, ...extras }
 *                                   (calls EP-D1 `PUT .../death-record`); body = { death_at,
 *                                   declaring_doctor_member_id, death_rev }
 * @param onSaved(admission)      the FULL admission from EP-D1's response — the caller
 *                                   re-renders every flag surface off this ONE payload,
 *                                   never refetches (S5)
 */
export default function RecordDeathDialog({ admission, declaringDoctorOptions = [], onClose, onSave, onSaved }) {
  const existing = admission.cause_of_death || null;
  const isEdit = !!existing;

  // D1b — the stored value is a UTC instant; the control is READ by the
  // desk as LOCAL time (same convention `AdmitPatientForm`'s `admittedAt`
  // uses). `String(...).slice(0, 16)` on a raw "...Z" string showed the
  // UTC clock in a control the desk reads as local — a stored 02:14Z
  // (07:44 IST) rendered as "02:14", 5h30m off the real time of death.
  // `toDatetimeLocalValue(new Date(existing.death_at))` converts the
  // INSTANT to the browser's local wall clock, same as every other
  // datetime-local prefill in this codebase.
  const [deathAt, setDeathAt] = useState(
    existing && existing.death_at
      ? toDatetimeLocalValue(new Date(existing.death_at))
      : toDatetimeLocalValue(new Date()),
  );
  const [doctorId, setDoctorId] = useState(
    (existing && existing.declaring_doctor_member_id) || '',
  );
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const canSubmit = !!deathAt && !!doctorId;

  // K11 — the discharge date IS the recorded time of death (FY-fallback notice if another FY).
  const deathIso = deathAt && !Number.isNaN(new Date(deathAt).getTime()) ? new Date(deathAt).toISOString() : null;
  const dischargeDateLine = !deathIso ? null
    : deathDodFyFallback({ deathAt: deathIso, fyAnchor: admission.invoice_fy }) ? DOD_DEATH_FY_FALLBACK
      : dodDeathLine(deathIso);

  const bedLine = admission.bed_label
    ? `${admission.ward_name ? `${admission.ward_name} · ` : ''}Bed ${admission.bed_label}`
    : (isAwaitingBed(admission) ? 'Awaiting bed' : 'No bed assigned');

  const runSave = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    const res = await onSave({
      // D1 — `deathAt` is the datetime-local control's NAIVE local
      // wall-clock string; the server (`_validate_death_at`) requires a
      // TIMEZONE-AWARE instant. Convert at this boundary, same pattern
      // `AdmitPatientForm`'s backdated `admitted_at` already uses:
      // `new Date(naiveLocal).toISOString()` reads the string in the
      // BROWSER's local zone and serialises it as a UTC instant.
      death_at: new Date(deathAt).toISOString(),
      declaring_doctor_member_id: doctorId,
      death_rev: admission.death_rev || 0,
    });
    setBusy(false);
    setConfirming(false);
    if (res.success) {
      if (typeof onSaved === 'function') onSaved(res.data.admission);
      onClose();
      return;
    }
    setError(
      // [NEEDS-PO] — every branch below is a placeholder pending PO copy.
      res.code === DEATH_RECORD_ERROR_CODE.DEATH_RECORD_STALE ? 'This record has changed since you opened it. Close and reopen to see the latest.'
        : res.code === DEATH_RECORD_ERROR_CODE.ADMISSION_CLOSED ? 'This admission is no longer open — a death cannot be recorded or edited on it.'
          : res.code === DEATH_RECORD_ERROR_CODE.DEATH_AT_INVALID ? 'The date & time of death must fall between admission and now.'
            : res.code === DEATH_RECORD_ERROR_CODE.DECLARING_DOCTOR_INVALID ? 'Choose an active doctor to declare this death.'
              : res.error || 'That action could not be completed.',
    );
  };

  const confirmTitle = isEdit
    ? `Save changes to the death record for ${admission.patient_name || admission.patient_id}?`
    : `Record death for ${admission.patient_name || admission.patient_id}?`;
  // Consequences-not-warnings copy. The billing line is RATIFIED BY THE OWNER
  // (2026-08-27); the rest still carries the mockup's wording pending PO.
  //
  // It replaced "Clinical orders, labs and bed-day charges continue as normal
  // — recording a death does not stop them", which was wrong twice over. It
  // read as though CARE continues for a patient who has just died, when the
  // only reason anything stays open is that a few trailing charges may still
  // need to go on the bill. And it promised automatic bed-day charges that a
  // pilot org never posts — `bed_day_accrual_enabled` is off, and
  // `RunningBillPanel`'s own note tells the same staff the opposite ("Room and
  // bed charges are added manually"). Two screens, one admission, contradictory
  // promises about money.
  //
  // Scope is now stated as billing and nothing else. Do not re-broaden it to
  // clinical activity without the owner: "continues as normal" is the phrase
  // that made this wrong.
  const confirmBody = isEdit ? (
    <ul className="list-disc pl-4 space-y-1 text-left">
      <li>The recorded date/time and declaring doctor on this admission will be updated.</li>
      <li>The discharge death block will reflect the updated values once you complete discharge.</li>
    </ul>
  ) : (
    <ul className="list-disc pl-4 space-y-1 text-left">
      <li>This admission will be flagged and highlighted until discharge is completed.</li>
      <li>At discharge, the death record auto-fills and the discharge type is locked to Deceased.</li>
      <li>The bill stays open so any remaining charges can still be added.</li>
      <li>You can edit or remove this record any time before discharge.</li>
    </ul>
  );

  return (
    <div className={overlayCls} data-testid="record-death-dialog">
      <div className={panelCls}>
        <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center">
          <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
            {isEdit ? 'Edit death record' : 'Record death'}
          </h3>
          <button type="button" onClick={onClose} className="ml-auto text-slate-500 dark:text-slate-400" aria-label="Close">✕</button>
        </div>
        <div className="p-4 space-y-4">
          {/* Identity strip — bed is the strongest wrong-patient discriminator (D40). */}
          <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-[12.5px] text-slate-600 dark:text-slate-300 px-3 py-2 flex flex-wrap gap-x-2 gap-y-1" data-testid="record-death-identity">
            <b className="text-slate-900 dark:text-white">{admission.patient_name || admission.patient_id}</b>
            {/* D34/D35 — the identity strip is a wrong-patient GUARD, never a money
                document: `resolveUhid`'s `deriveUhid(patient_id)` fallback (a
                fragment of a database key, e.g. "A3B75") is deliberately NOT
                reused here. A pre-2026-08-03 patient genuinely has no UHID
                (D35, no backfill) — labelling that plainly is safer than a
                fake identifier sitting beside the strongest wrong-patient
                discriminator on this screen. [NEEDS-PO] label copy. */}
            <span data-testid="record-death-uhid">
              · {admission.uhid ? admission.uhid : 'No UHID on file'}
            </span>
            <span>· {admission.ip_number}</span>
            <span className="px-2 py-0.5 rounded-md bg-teal-100 dark:bg-teal-900/40 border border-teal-300 dark:border-teal-700 text-teal-800 dark:text-teal-200 font-semibold" data-testid="record-death-bed-badge">
              {bedLine}
            </span>
          </div>

          {error && (
            <div className="rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 text-[12.5px] p-2.5" data-testid="record-death-error">
              {error}
            </div>
          )}

          <div>
            {/* [NEEDS-PO] label copy: "Date & time of death" vs "Time of death". */}
            <label className={labelCls} htmlFor="record-death-at">Date &amp; time of death</label>
            <input
              id="record-death-at"
              className={inputCls}
              type="datetime-local"
              value={deathAt}
              onChange={(e) => setDeathAt(e.target.value)}
              data-testid="record-death-at"
            />
            {dischargeDateLine && (
              <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1" data-testid="record-death-discharge-date-line">
                {dischargeDateLine}
              </p>
            )}
          </div>

          <div>
            {/* [NEEDS-PO] label copy. */}
            <label className={labelCls} htmlFor="record-death-doctor">Declaring doctor (required)</label>
            <select
              id="record-death-doctor"
              className={inputCls}
              value={doctorId}
              onChange={(e) => setDoctorId(e.target.value)}
              data-testid="record-death-doctor"
            >
              <option value="">Select the declaring doctor…</option>
              {declaringDoctorOptions.map((d) => (
                <option key={d.member_id} value={d.member_id}>{d.name}</option>
              ))}
            </select>
            <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">
              The certifying medical attendant — required for the death certificate, and a safety control
              against recording against the wrong patient.
            </p>
          </div>
        </div>

        <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-2">
          <button type="button" className={btnSecondary} onClick={onClose}>Close</button>
          <button
            type="button"
            className={btnPrimary}
            onClick={() => setConfirming(true)}
            disabled={!canSubmit}
            data-testid="record-death-submit-open"
          >
            {isEdit ? 'Save changes' : 'Record death'}
          </button>
        </div>
      </div>

      <ConfirmationModal
        isOpen={confirming}
        title={confirmTitle}
        message={confirmBody}
        ctaLabel={busy ? 'Saving…' : (isEdit ? 'Save changes' : 'Record death')}
        cancelLabel="Go back"
        onConfirm={runSave}
        onCancel={() => (busy ? null : setConfirming(false))}
      />
    </div>
  );
}
